import React, { forwardRef } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type { BaseInputProps } from './shared';
import { inputBaseClasses, inputErrorClasses } from './shared';

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>, BaseInputProps {
  /** Icône à gauche */
  leftIcon?: React.ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      id,
      errors = [],
      helpText,
      containerClassName = '',
      className = '',
      leftIcon,
      disabled,
      readOnly,
      required,
      ...props
    },
    ref
  ) => {
    const hasErrors = errors.length > 0;
    const inputId = id || `text-input-${Math.random().toString(36).slice(2)}`;

    const inputClasses = [
      inputBaseClasses,
      hasErrors ? inputErrorClasses : '',
      leftIcon ? 'pl-10' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`space-y-token-xs ${containerClassName}`}>
        {label && (
          <label htmlFor={inputId} className="block type-caption-text font-medium text-ink">
            {label}
            {required && <span className="text-token-danger ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-token-sm flex items-center pointer-events-none text-muted">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            type="text"
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={hasErrors}
            aria-describedby={
              hasErrors ? `${inputId}-errors` : helpText ? `${inputId}-help` : undefined
            }
            aria-label={label || props['aria-label']}
            className={inputClasses}
            {...props}
          />
        </div>
        {hasErrors && (
          <div id={`${inputId}-errors`} className="space-y-1" role="alert">
            {errors.map((error, index) => (
              <p
                key={index}
                className="type-caption-text text-token-danger flex items-center gap-1"
              >
                <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
                {error}
              </p>
            ))}
          </div>
        )}
        {helpText && !hasErrors && (
          <p id={`${inputId}-help`} className="type-caption-text text-muted">
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';
